import React, { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { ShieldCheck, Loader2, CheckCircle2, XCircle, Clock } from "lucide-react";
import api from "lib/api";
import { useI18n } from "i18n";
import LivenessGate from "pages/LivenessModal";

// Public hosted verification page (/v/:token). Authorized solely by the opaque
// session token in the URL (the OAuth-style redirect target). Runs the liveness
// gate; on success it redirects back to the caller with the one-time code, or
// shows a terminal state when there is no redirect.
export default function VerifyPage() {
  const { token } = useParams();
  const { setPublicLanguage } = useI18n();
  const [state, setState] = useState("loading"); // loading | ready | passed | expired | notfound | error
  const [requireGeo, setRequireGeo] = useState(false);

  useEffect(() => {
    if (token) api.defaults.headers.common["X-Session-Token"] = token;
    let alive = true;
    (async () => {
      try {
        const res = await api.get(`/verify/api/status/`);
        const d = res.data || {};
        if (!alive) return;
        // Public policy: the user's browser language when we ship it, else the
        // session/tenant language the client set at /start.
        setPublicLanguage(d.language);
        if (d.status === "failed") setState("denied");   // terminal — immutable
        else if (d.expired) setState("expired");
        else if (d.passed) setState("passed");
        else { setRequireGeo(!!d.require_geo); setState("ready"); }
      } catch (e) {
        if (!alive) return;
        const s = e?.response?.status;
        setState(s === 410 ? "expired" : s === 404 ? "notfound" : "error");
      }
    })();
    return () => { alive = false; };
  }, [token, setPublicLanguage]);

  const onPassed = useCallback((redirect) => {
    if (redirect) { window.location.href = redirect; return; }
    setState("passed");
  }, []);

  // Terminal DENIED verdict: redirect back to the client system when it gave a
  // redirect_uri; otherwise show the terminal screen. Retrying requires the
  // client to open a brand-new verification (/start).
  const onDenied = useCallback((redirect) => {
    if (redirect) { window.location.href = redirect; return; }
    setState("denied");
  }, []);

  const Shell = ({ children }) => (
    <div className="min-h-screen w-full bg-background text-foreground flex flex-col items-center justify-center p-4">
      <div className="w-full max-w-md space-y-6">
        <div className="flex items-center justify-center gap-2 text-primary">
          <ShieldCheck className="h-6 w-6" />
          <span className="text-lg font-semibold">SecFace</span>
        </div>
        {children}
      </div>
    </div>
  );

  const Notice = ({ icon: Icon, tone, title, body }) => (
    <div className="rounded-2xl border border-border bg-card p-8 text-center space-y-3">
      <Icon className={`h-12 w-12 mx-auto ${tone}`} />
      <h1 className="text-lg font-semibold">{title}</h1>
      <p className="text-sm text-muted-foreground">{body}</p>
    </div>
  );

  if (state === "loading") {
    return <Shell><Notice icon={Loader2} tone="text-primary animate-spin"
      title="Carregando…" body="Preparando a verificação de identidade." /></Shell>;
  }
  if (state === "passed") {
    return <Shell><Notice icon={CheckCircle2} tone="text-emerald-500"
      title="Verificação concluída" body="Sua identidade foi verificada. Você já pode fechar esta janela." /></Shell>;
  }
  if (state === "denied") {
    return <Shell><Notice icon={XCircle} tone="text-red-500"
      title="Verificação não aprovada"
      body="Esta verificação foi negada e não pode ser refeita neste link. Volte ao sistema de origem e inicie uma nova verificação." /></Shell>;
  }
  if (state === "expired") {
    return <Shell><Notice icon={Clock} tone="text-amber-500"
      title="Sessão expirada" body="Este link de verificação expirou. Solicite um novo para continuar." /></Shell>;
  }
  if (state === "notfound") {
    return <Shell><Notice icon={XCircle} tone="text-red-500"
      title="Sessão não encontrada" body="Este link de verificação é inválido." /></Shell>;
  }
  if (state === "error") {
    return <Shell><Notice icon={XCircle} tone="text-red-500"
      title="Algo deu errado" body="Não foi possível iniciar a verificação. Tente novamente mais tarde." /></Shell>;
  }
  return (
    <Shell>
      <LivenessGate token={token} requireGeo={requireGeo}
        onPassed={onPassed} onDenied={onDenied} />
    </Shell>
  );
}
